import { BoxWrapper, Button } from 'components/Base';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import RiskTableRow from './RiskTableRow';
import selectors from 'store/selectors';
import '../riskMapping.scss';
import { Chip, CircularProgress } from '@material-ui/core';
import CheckRoundedIcon from '@material-ui/icons/CheckRounded';
import ClearRoundedIcon from '@material-ui/icons/ClearRounded';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import {
  greenBackground,
  greenText,
  redBackground,
  redText
} from 'utils/colors';
import moment from 'moment';
import Charts from 'components/Charts';
import { comfortRangeConfig } from '../utils';

const useStyles = makeStyles(() =>
  createStyles({
    greenChip: {
      backgroundColor: greenBackground,
      color: greenText
    },
    redChip: {
      backgroundColor: redBackground,
      color: redText
    },
    pendingNote: {
      fontSize: '11px',
      width: '450px',
      paddingLeft: '0.5rem'
    }
  })
);

type RiskMapTableProps = {
  cip: any;
  riskMapData: any;
  firmRiskGroup?: number;
  riskMapSelected: Function;
  isFirmAdmin: boolean;
  riskProfiler: string;
};

const RiskMapTable: React.FC<RiskMapTableProps> = ({
  cip,
  riskMapData,
  firmRiskGroup,
  riskMapSelected,
  isFirmAdmin,
  riskProfiler
}) => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null as any);
  const [statusComponent, setStatusComponent] = useState((<div></div>) as any);
  const [comfortRanges, setComfortRanges] = useState([] as any);

  const extractRiskMapData = (timeHorizon: number, riskCategory: number) => {
    if (riskMapData[timeHorizon] && riskMapData[timeHorizon][riskCategory]) {
      return riskMapData[timeHorizon][riskCategory];
    } else {
      return 'not found';
    }
  };

  const riskMap = () => {
    setIsLoading(true);
    riskMapSelected([Number(cip.value)]);
  };

  const statuses = [
    {
      key: 'pending',
      display: 'Mapping',
      icon: <CircularProgress size={12} />
    },
    {
      key: 'succeeded',
      display: 'Mapped',
      icon: <CheckRoundedIcon style={{ fill: greenText }} />,
      class: classes.greenChip
    },
    {
      key: 'failed',
      display: 'Failed',
      icon: <ClearRoundedIcon style={{ fill: redText }} />,
      class: classes.redChip
    }
  ];

  useEffect(() => {
    setCurrentStatus(statuses.find(status => status.key === cip.status));
  }, [cip]);

  useEffect(() => {
    if (!currentStatus) {
      return;
    }
    if (currentStatus.key === 'pending') {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }

    setStatusComponent(() => (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Chip
          className={currentStatus.class}
          size="small"
          icon={currentStatus.icon}
          label={
            currentStatus.display.charAt(0).toUpperCase() +
            currentStatus.display.slice(1)
          }
        />
        {currentStatus.key === 'pending' ? (
          <div className={classes.pendingNote}>
            This process may take some time. You can safely navigate away and
            will receive a notification once risk mapping is complete.
          </div>
        ) : (
          <></>
        )}
      </div>
    ));
  }, [currentStatus]);

  useEffect(() => {
    if (riskProfiler === 'EValue') {
      return;
    }

    setComfortRanges(
      [...riskMapData.funds, ...riskMapData.model_portfolios]
        .map((investment: any) => {
          const bands = {} as any;
          Object.keys(comfortRangeConfig).forEach((key: string) => {
            bands[comfortRangeConfig[key]] =
              investment.comfort_ranges && investment.comfort_ranges[key]
                ? investment.comfort_ranges[key].high -
                  Math.max(0, investment.comfort_ranges[key].low - 1)
                : null;
          });

          return {
            name: investment.name,
            growth_assets: investment.comfort_ranges?.growth_asset_percentage,
            ...bands
          };
        })
        .sort((a, b) => {
          return a.growth_assets - b.growth_assets;
        })
    );
  }, [riskProfiler, riskMapData]);

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        margin: '2rem 0'
      }}
    >
      <BoxWrapper
        title={cip.name}
        headerChildren={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginLeft: '1rem',
              width: '100%',
              justifyContent: 'space-between'
            }}
          >
            {statusComponent}
            <div>
              {cip.risk_mapped_at ? (
                <>
                  Last updated <b>{moment(cip.risk_mapped_at).fromNow()}</b>
                </>
              ) : (
                <></>
              )}
            </div>
          </div>
        }
      >
        {riskProfiler !== 'EValue' ? (
          <div style={{ padding: '0 1rem' }}>
            <Charts.MorningstarRiskProfiler.ComfortRanges
              data={comfortRanges}
            />
          </div>
        ) : (
          <div
            style={{
              width: '100%',
              minHeight: '300px',
              padding: '0 1rem',
              borderRadius: '8px 8px 0px 0px',
              overflow: 'hidden'
            }}
          >
            <div
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                borderRadius: '4px',
                opacity: isLoading ? 0.5 : 1
              }}
            >
              <div className="titleRow"></div>
              <div className="titleRow">3-5yrs</div>
              <div className="titleRow">6-10yrs</div>
              <div className="titleRow">11-15yrs</div>
              <div className="titleRow">16-20yrs</div>
              <div className="titleRow">21+yrs</div>
            </div>
            <div style={{ opacity: isLoading ? 0.5 : 1 }}>
              {Array.from(Array(firmRiskGroup)).map((riskGroup, i) => (
                <RiskTableRow
                  key={`Risk Category Row ${i + 1}`}
                  threeToFive={extractRiskMapData(305, i + 1)}
                  sixToTen={extractRiskMapData(610, i + 1)}
                  elevenToFifteen={extractRiskMapData(1115, i + 1)}
                  sixteenToTwenty={extractRiskMapData(1620, i + 1)}
                  twentyOnePlus={extractRiskMapData(2100, i + 1)}
                  riskCategory={i + 1}
                />
              ))}
            </div>
          </div>
        )}
        <div className="buttonContainer">
          {cip.status === 'pending' ? statusComponent : <div></div>}
          {cip.can_be_risk_mapped && isFirmAdmin && (
            <Button
              disabled={isLoading}
              isLoading={isLoading}
              key={`Risk Map ${cip.name}`}
              onClick={() => {
                if (isLoading) return;
                riskMap();
              }}
              type="button"
            >
              {`Risk Map ${cip.name}`}
            </Button>
          )}
        </div>
      </BoxWrapper>
    </div>
  );
};

export default connect((state: any) => ({
  firmRiskGroup: selectors.firmSelectors.riskGroup(state),
  isFirmAdmin: selectors.advisorSelectors.isFirmAdmin(state),
  riskProfiler: selectors.firmSelectors.riskProfiler(state)
}))(RiskMapTable);
